import PropTypes from "prop-types";
import { useIsLightMode, useCurrency } from "../../../utils/helpers";
import BarChartWithCurvedEdges from "./barChartWithCurvedEdges";
import IndicatorHighlights from "./indicatorHighlights";

import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const HighlightsIndicator = ({ data, scenarioData, kpiFactorsData, cashflowIndicatorData }) => {
  const isLightMode = useIsLightMode();
  const currency = useCurrency();
  const { t } = useTranslation();

  const savingsColor = (savings) => (savings > 0 ? "#B7E4C7" : "#D9D9D9");
  const indicatorHeaders = useMemo(
    () => [
      {
        title: t('result.highlights.capitalExpenditure', { currency }),
        categories: ["Photo Voltaic", "Genset", "Battery", "Controls"],
        data: [
          data?.pvCapex,
          data?.gensetCapex,
          data?.batteryCapex,
          data?.controllerCapex,
        ],
        minWidth: 400,
      },
      {
        title: t('result.highlights.annualElectricityBill', { currency }),
        categories: ["Pre Microgrid", "Post Microgrid", "Savings"],
        data: [
          data?.refElectricBillCharge,
          data?.electricBillCharge,
          (data?.refElectricBillCharge || 0) - (data?.electricBillCharge || 0),
        ],
        barColors: [
          "#0F81B3",
          "#42B4E6",
          savingsColor((data?.refElectricBillCharge || 0) - (data?.electricBillCharge || 0)),
        ],
      },
      {
        title: t('result.highlights.netPresentCost', { currency }),
        categories: ["Pre Microgrid", "Post Microgrid", "Variation"],
        data: [
          data?.refNetPresentCost,
          data?.netPresentCost,
          (data?.refNetPresentCost || 0) - (data?.netPresentCost || 0),
        ],
        barColors: [
          "#0F81B3",
          "#42B4E6",
          savingsColor((data?.refNetPresentCost || 0) - (data?.netPresentCost || 0)),
        ],
      },
      {
        title: t('result.highlights.levelizedCostOfEnergy', { currency }),
        categories: ["Pre Microgrid", "Post Microgrid", "Variation"],
        data: [
          data?.refLcoeUtility,
          data?.lcoeUtility,
          (data?.refLcoeUtility || 0) - (data?.lcoeUtility || 0),
        ],
        barColors: [
          "#0F81B3",
          "#42B4E6",
          savingsColor((data?.refLcoeUtility || 0) - (data?.lcoeUtility || 0)),
        ],
      },
      {
        title: t('result.highlights.annualCo2Emissions'),
        categories: ["Pre Microgrid", "Post Microgrid", "Variation"],
        data: [
          data?.refCo2Emission,
          data?.co2Emission,
          (data?.refCo2Emission || 0) - (data?.co2Emission || 0),
        ],
        barColors: [
          "#0F81B3",
          "#42B4E6",
          savingsColor((data?.refCo2Emission || 0) - (data?.co2Emission || 0)),
        ],
      },
      {
        title: t('result.highlights.aggregateProjectExpenditure', { currency }),
        categories: ["Pre Microgrid", "Post Microgrid", "Savings"],
        data: [
          cashflowIndicatorData?.preCashflowTotal,
          cashflowIndicatorData?.postCashflowTotal,
          (cashflowIndicatorData?.preCashflowTotal || 0) -
            (cashflowIndicatorData?.postCashflowTotal || 0),
        ],
        barColors: [
          "#0F81B3",
          "#42B4E6",
          savingsColor(
            (cashflowIndicatorData?.preCashflowTotal || 0) -
              (cashflowIndicatorData?.postCashflowTotal || 0)
          ),
        ],
      },
    ],
    [data, currency, cashflowIndicatorData, t]
  );

  return (
    <>
      <div
      className=" d-flex flex-column w-100 sar"
        style={{
          backgroundColor: !isLightMode && "var(--se-background-alternative)",
        }}
      >
     
        <IndicatorHighlights scenarioData={scenarioData} kpiFactorsData={kpiFactorsData} />
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "3rem",
            width: "100%",
          }}
        >
          {indicatorHeaders?.map((config, index) => (
            <span key={index} style={{ flexBasis: "30%" }}>
             { <BarChartWithCurvedEdges
                title={config.title}
                categories={config.categories}
                data={config.data}
                barColors={config.barColors}
              />}
            </span>
          ))}
        </div>
      </div>
    </>
  );
};

HighlightsIndicator.propTypes = {
  data: PropTypes.shape({
    netPresentCost: PropTypes.number,
    refNetPresentCost: PropTypes.number,
    lcoeUtility: PropTypes.number,
    refLcoeUtility: PropTypes.number,
    co2Emission: PropTypes.number,
    refCo2Emission: PropTypes.number,
    payback: PropTypes.number,
    roi: PropTypes.number,
  }),
  scenarioData: PropTypes.shape({
    Capital: PropTypes.number,
    ROI: PropTypes.number,
    IRR: PropTypes.number,
    PaybackTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  cashflowIndicatorData: PropTypes.shape({
    preCashflowTotal: PropTypes.number,
    postCashflowTotal: PropTypes.number,
  }),
};

export default HighlightsIndicator;
